if (!customElements.get('shipping-estimator')) {
  customElements.define(
    'shipping-estimator',
    class ShippingEstimator extends HTMLElement {
      constructor() {
        super();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleSearchInput = this.handleSearchInput.bind(this);
        this.handleSearchKeyDown = this.handleSearchKeyDown.bind(this);
        this.handleOptionClick = this.handleOptionClick.bind(this);
        this.handleOptionKeyDown = this.handleOptionKeyDown.bind(this);
        this.handleResetClick = this.handleResetClick.bind(this);
        this.handleProductChange = this.handleProductChange.bind(this);
        this.handleVariantEvent = this.handleVariantEvent.bind(this);
        this.handleCartEvent = this.handleCartEvent.bind(this);
        this.hydrateCartState = this.hydrateCartState.bind(this);
        this.unsubscribe = [];
      }

      connectedCallback() {
        this.context = this.dataset.context;
        this.config = this.readJson('[data-shipping-config]');
        this.state = this.readJson('[data-shipping-state]');
        this.locale = this.state?.locale || document.documentElement.lang || 'en';
        this.strings = this.resolveStrings(this.readJson('[data-shipping-strings]'));
        this.shell = this.querySelector(`.shipping-estimator__shell--${this.context}`) || this.querySelector('.shipping-estimator__shell');

        if (!this.context || !this.config || !this.state || !this.strings || !this.shell) return;

        this.displayNames = this.createDisplayNames();
        this.countries = Array.isArray(this.state.countries) ? this.state.countries : [];
        this.selectedCountryCode = this.state.countryCode || this.countries[0]?.code || '';
        this.productInfo = this.closest('product-info');

        this.elements = {
          title: this.querySelector('[data-role="title"]'),
          button: this.querySelector('.shipping-estimator__toggle'),
          panel: this.querySelector('.shipping-estimator__panel'),
          search: this.querySelector('.shipping-estimator__search'),
          searchInput: this.querySelector('[data-role="search-input"]'),
          searchLabel: this.querySelector('[data-role="search-label"]'),
          reset: this.querySelector('.shipping-estimator__reset'),
          list: this.querySelector('.shipping-estimator__options'),
          empty: this.querySelector('[data-role="empty"]'),
          liveRegion: this.querySelector('[data-role="live-region"]'),
          countryLabel: this.querySelector('[data-role="country-label"]'),
          selectedCountry: this.querySelectorAll('[data-role="selected-country"]'),
          rateRow: this.querySelector('[data-role="rate-row"]'),
          rateName: this.querySelector('[data-role="rate-name"]'),
          rateDetail: this.querySelector('[data-role="rate-detail"]'),
          primary: this.querySelector('[data-role="primary"]'),
          secondary: this.querySelector('[data-role="secondary"]'),
          rates: this.querySelector('[data-role="rates"]'),
          message: this.querySelector('[data-role="message"]'),
          progressBlock: this.querySelector('[data-role="progress-block"]'),
          progress: this.querySelector('.shipping-estimator__progress'),
          progressText: this.querySelector('[data-role="progress-text"]'),
          progressFill: this.querySelector('[data-role="progress-fill"]'),
          progressGoal: this.querySelector('[data-role="progress-goal"]'),
          progressGoalText: this.querySelector('[data-role="progress-goal-text"]'),
          countryInput: this.querySelector('[data-role="country-code-input"]'),
        };
        this.localizationForm = this.elements.countryInput?.form || this.querySelector('form');

        this.applyStaticText();
        this.renderCountryOptions();
        this.bindEvents();
        this.render();
        this.hydrateCartState();
        this.shell.hidden = false;
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        this.elements?.button?.removeEventListener('click', this.handleButtonClick);
        this.elements?.search?.removeEventListener('input', this.handleSearchInput);
        this.elements?.search?.removeEventListener('keydown', this.handleSearchKeyDown);
        this.elements?.reset?.removeEventListener('click', this.handleResetClick);
        this.elements?.list?.querySelectorAll('[data-country-code]').forEach((option) => {
          option.removeEventListener('click', this.handleOptionClick);
          option.removeEventListener('keydown', this.handleOptionKeyDown);
        });
        this.productInfo?.removeEventListener('change', this.handleProductChange);
        this.unsubscribe.forEach((stop) => stop?.());
        this.unsubscribe = [];
      }

      bindEvents() {
        this.elements.button?.addEventListener('click', this.handleButtonClick);
        this.elements.search?.addEventListener('input', this.handleSearchInput);
        this.elements.search?.addEventListener('keydown', this.handleSearchKeyDown);
        this.elements.reset?.addEventListener('click', this.handleResetClick);
        document.addEventListener('click', this.handleDocumentClick);

        this.elements.list?.querySelectorAll('[data-country-code]').forEach((option) => {
          option.addEventListener('click', this.handleOptionClick);
          option.addEventListener('keydown', this.handleOptionKeyDown);
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (this.context === 'product') {
            this.unsubscribe.push(subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantEvent));
          }

          if (this.state.cart) {
            this.unsubscribe.push(subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartEvent));
          }
        }

        if (this.context === 'product' && this.productInfo) {
          this.productInfo.addEventListener('change', this.handleProductChange);
        }
      }

      readJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return null;

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('Shipping estimator JSON parse error', error);
          return null;
        }
      }

      resolveStrings(rawStrings) {
        const defaults = this.getDefaultStrings();

        return Object.keys(defaults).reduce((resolved, key) => {
          const value = rawStrings?.[key];
          resolved[key] = this.isBrokenTranslation(value) ? defaults[key] : value;
          return resolved;
        }, {});
      }

      getDefaultStrings() {
        const language = this.getLanguageCode();
        const defaults = {
          it: {
            title: 'Stima costi di spedizione',
            countryLabel: 'Spedisci a',
            freeShipping: 'Spedizione gratuita',
            freeShippingRemaining: 'Ti mancano {{ amount }} per la spedizione gratuita',
            noShipping: 'Spedizione non disponibile per questo paese',
            noWeight: 'Contattaci per un preventivo di spedizione',
            searchCountry: 'Cerca paese...',
            searchEmpty: 'Nessun paese trovato',
            freeShippingSuccess: 'Complimenti! Hai ottenuto la spedizione gratuita!',
            freeBadge: 'Gratis',
          },
          de: {
            title: 'Versandkostenschätzung',
            countryLabel: 'Versand nach',
            freeShipping: 'Kostenloser Versand',
            freeShippingRemaining: 'Noch {{ amount }} bis zum kostenlosen Versand',
            noShipping: 'Versand in dieses Land nicht verfügbar',
            noWeight: 'Kontaktieren Sie uns für ein Versandangebot',
            searchCountry: 'Land suchen...',
            searchEmpty: 'Keine Länder gefunden',
            freeShippingSuccess: 'Glückwunsch! Du hast kostenlosen Versand erhalten!',
            freeBadge: 'Gratis',
          },
          fr: {
            title: 'Estimation des frais de port',
            countryLabel: 'Livraison vers',
            freeShipping: 'Livraison gratuite',
            freeShippingRemaining: 'Plus que {{ amount }} pour la livraison gratuite',
            noShipping: 'Livraison indisponible vers ce pays',
            noWeight: 'Contactez-nous pour un devis de livraison',
            searchCountry: 'Rechercher un pays...',
            searchEmpty: 'Aucun pays trouvé',
            freeShippingSuccess: 'Félicitations ! Vous avez obtenu la livraison gratuite !',
            freeBadge: 'Gratuit',
          },
          es: {
            title: 'Estimación de envío',
            countryLabel: 'Enviar a',
            freeShipping: 'Envío gratis',
            freeShippingRemaining: 'Te faltan {{ amount }} para el envío gratis',
            noShipping: 'Envío no disponible para este país',
            noWeight: 'Contáctanos para un presupuesto de envío',
            searchCountry: 'Buscar país...',
            searchEmpty: 'No se encontraron países',
            freeShippingSuccess: '¡Enhorabuena! Has conseguido el envío gratis.',
            freeBadge: 'Gratis',
          },
          pt: {
            title: 'Estimativa de envio',
            countryLabel: 'Enviar para',
            freeShipping: 'Envio grátis',
            freeShippingRemaining: 'Faltam {{ amount }} para envio grátis',
            noShipping: 'Envio indisponível para este país',
            noWeight: 'Contacte-nos para um orçamento de envio',
            searchCountry: 'Pesquisar país...',
            searchEmpty: 'Nenhum país encontrado',
            freeShippingSuccess: 'Parabéns! Você conseguiu frete grátis.',
            freeBadge: 'Grátis',
          },
          nl: {
            title: 'Verzendkosten schatting',
            countryLabel: 'Verzenden naar',
            freeShipping: 'Gratis verzending',
            freeShippingRemaining: 'Nog {{ amount }} tot gratis verzending',
            noShipping: 'Verzending niet beschikbaar voor dit land',
            noWeight: 'Neem contact met ons op voor een verzendofferte',
            searchCountry: 'Land zoeken...',
            searchEmpty: 'Geen landen gevonden',
            freeShippingSuccess: 'Gefeliciteerd! Je hebt gratis verzending bereikt!',
            freeBadge: 'Gratis',
          },
          pl: {
            title: 'Szacowany koszt wysyłki',
            countryLabel: 'Wyślij do',
            freeShipping: 'Darmowa dostawa',
            freeShippingRemaining: 'Do darmowej dostawy brakuje {{ amount }}',
            noShipping: 'Wysyłka do tego kraju jest niedostępna',
            noWeight: 'Skontaktuj się z nami, aby otrzymać wycenę wysyłki',
            searchCountry: 'Szukaj kraju...',
            searchEmpty: 'Nie znaleziono krajów',
            freeShippingSuccess: 'Gratulacje! Otrzymałeś darmową dostawę!',
            freeBadge: 'Gratis',
          },
          cs: {
            title: 'Odhad nákladů na dopravu',
            countryLabel: 'Odeslat do',
            freeShipping: 'Doprava zdarma',
            freeShippingRemaining: 'K dopravě zdarma vám chybí {{ amount }}',
            noShipping: 'Doprava do této země není k dispozici',
            noWeight: 'Kontaktujte nás pro cenovou nabídku dopravy',
            searchCountry: 'Hledat zemi...',
            searchEmpty: 'Nenalezena žádná země',
            freeShippingSuccess: 'Gratulujeme! Získali jste dopravu zdarma!',
            freeBadge: 'Free',
          },
          sk: {
            title: 'Odhad nákladov na dopravu',
            countryLabel: 'Poslať do',
            freeShipping: 'Doprava zadarmo',
            freeShippingRemaining: 'Do dopravy zadarmo vám chýba {{ amount }}',
            noShipping: 'Doprava do tejto krajiny nie je dostupná',
            noWeight: 'Kontaktujte nás pre cenovú ponuku dopravy',
            searchCountry: 'Hľadať krajinu...',
            searchEmpty: 'Nenašla sa žiadna krajina',
            freeShippingSuccess: 'Gratulujeme! Získali ste dopravu zadarmo!',
            freeBadge: 'Free',
          },
          hu: {
            title: 'Szállítási költség becslése',
            countryLabel: 'Szállítás ide',
            freeShipping: 'Ingyenes szállítás',
            freeShippingRemaining: 'Még {{ amount }} kell az ingyenes szállításhoz',
            noShipping: 'A szállítás ebbe az országba nem érhető el',
            noWeight: 'Vegye fel velünk a kapcsolatot szállítási ajánlatért',
            searchCountry: 'Ország keresése...',
            searchEmpty: 'Nem található ország',
            freeShippingSuccess: 'Gratulálunk! Elérted az ingyenes szállítást!',
            freeBadge: 'Ingyen',
          },
          en: {
            title: 'Shipping estimate',
            countryLabel: 'Ship to',
            freeShipping: 'Free shipping',
            freeShippingRemaining: '{{ amount }} away from free shipping',
            noShipping: 'Shipping unavailable to this country',
            noWeight: 'Contact us for a shipping quote',
            searchCountry: 'Search country...',
            searchEmpty: 'No countries found',
            freeShippingSuccess: 'Congratulations! You unlocked free shipping!',
            freeBadge: 'Free',
          },
        };

        return defaults[language] || defaults.en;
      }

      isBrokenTranslation(value) {
        if (typeof value !== 'string' || !value.trim()) return true;
        return /[ÃÂ�]/.test(value) || /\?/.test(value);
      }

      getLanguageCode() {
        return `${this.locale || ''}`.toLowerCase().split('-')[0];
      }

      createDisplayNames() {
        if (!window.Intl?.DisplayNames) return null;

        try {
          return new Intl.DisplayNames([this.locale], { type: 'region' });
        } catch (error) {
          return null;
        }
      }

      applyStaticText() {
        if (this.elements.title) {
          this.elements.title.textContent = this.strings.title;
        }

        if (this.elements.countryLabel) {
          this.elements.countryLabel.textContent = this.strings.countryLabel;
        }

        if (this.elements.searchInput) {
          this.elements.searchInput.placeholder = this.strings.searchCountry;
        }

        if (this.elements.searchLabel) {
          this.elements.searchLabel.textContent = this.strings.searchCountry;
        }

        if (this.elements.empty) {
          this.elements.empty.textContent = this.strings.searchEmpty;
        }

        if (this.elements.progressGoalText) {
          this.elements.progressGoalText.textContent = this.strings.freeBadge;
        }
      }

      renderCountryOptions() {
        if (!this.elements.list) return;

        this.elements.list.innerHTML = this.countries
          .map((country) => {
            const countryName = this.escapeHtml(this.getCountryName(country.code));
            const optionId = `${this.dataset.context}-${this.dataset.sectionId}-${country.code}`;

            return `
              <li class="shipping-estimator__option-item" role="presentation">
                <button
                  type="button"
                  id="${optionId}"
                  class="shipping-estimator__option"
                  role="option"
                  aria-selected="false"
                  data-country-code="${this.escapeHtml(country.code)}"
                >
                  ${countryName}
                </button>
              </li>
            `;
          })
          .join('');
      }

      handleButtonClick(event) {
        event.preventDefault();

        if (this.elements.panel?.hasAttribute('hidden')) {
          this.openPanel();
        } else {
          this.closePanel();
        }
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.closePanel();
        }
      }

      openPanel() {
        if (!this.elements.panel || !this.elements.button) return;

        this.elements.panel.hidden = false;
        this.elements.button.setAttribute('aria-expanded', 'true');
        this.elements.search?.setAttribute('aria-expanded', 'true');
        this.filterOptions('');
        requestAnimationFrame(() => this.elements.search?.focus());
      }

      closePanel() {
        if (!this.elements.panel || !this.elements.button) return;

        this.elements.panel.hidden = true;
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.search?.setAttribute('aria-expanded', 'false');

        if (this.elements.search) {
          this.elements.search.value = '';
          this.elements.search.setAttribute('aria-activedescendant', '');
        }

        this.filterOptions('');
      }

      handleSearchInput(event) {
        this.filterOptions(event.target.value);
      }

      handleResetClick(event) {
        event.preventDefault();
        if (!this.elements.search) return;

        this.elements.search.value = '';
        this.filterOptions('');
        this.elements.search.focus();
      }

      handleSearchKeyDown(event) {
        const visibleOptions = this.getVisibleOptions();

        switch (event.code.toUpperCase()) {
          case 'ARROWDOWN':
            event.preventDefault();
            visibleOptions[0]?.focus();
            this.syncActivedescendant(visibleOptions[0]);
            break;
          case 'ARROWUP':
            event.preventDefault();
            visibleOptions[visibleOptions.length - 1]?.focus();
            this.syncActivedescendant(visibleOptions[visibleOptions.length - 1]);
            break;
          case 'ENTER':
            if (visibleOptions.length === 1) {
              event.preventDefault();
              this.selectCountry(visibleOptions[0].dataset.countryCode);
            }
            break;
          case 'ESCAPE':
            event.preventDefault();
            this.closePanel();
            this.elements.button?.focus();
            break;
        }
      }

      handleOptionClick(event) {
        const option = event.currentTarget;
        this.selectCountry(option.dataset.countryCode);
      }

      handleOptionKeyDown(event) {
        const visibleOptions = this.getVisibleOptions();
        const currentIndex = visibleOptions.findIndex((option) => option === event.currentTarget);

        switch (event.code.toUpperCase()) {
          case 'ARROWDOWN':
            event.preventDefault();
            visibleOptions[(currentIndex + 1) % visibleOptions.length]?.focus();
            this.syncActivedescendant(visibleOptions[(currentIndex + 1) % visibleOptions.length]);
            break;
          case 'ARROWUP':
            event.preventDefault();
            visibleOptions[(currentIndex - 1 + visibleOptions.length) % visibleOptions.length]?.focus();
            this.syncActivedescendant(visibleOptions[(currentIndex - 1 + visibleOptions.length) % visibleOptions.length]);
            break;
          case 'ENTER':
          case 'SPACE':
            event.preventDefault();
            this.selectCountry(event.currentTarget.dataset.countryCode);
            break;
          case 'ESCAPE':
            event.preventDefault();
            this.closePanel();
            this.elements.button?.focus();
            break;
        }
      }

      handleProductChange(event) {
        if (!event.target.matches(`.quantity__input[form="product-form-${this.dataset.sectionId}"], #Quantity-${this.dataset.sectionId}`)) {
          return;
        }

        if (this.state.product) {
          this.state.product.quantity = this.getProductQuantity();
          this.render();
        }
      }

      handleVariantEvent(event) {
        const variantEvent = event?.data;
        if (!variantEvent || `${variantEvent.sectionId}` !== `${this.dataset.sectionId}` || !variantEvent.variant || !this.state.product) {
          return;
        }

        const variantId = `${variantEvent.variant.id}`;
        this.state.product.selectedVariantId = variantId;
        this.state.product.variants = this.state.product.variants || {};
        this.state.product.variants[variantId] = {
          priceCents: Number(variantEvent.variant.price || 0),
          weightGrams: Number(variantEvent.variant.weight || 0),
        };
        this.render();
      }

      async handleCartEvent(event) {
        if (!this.state.cart) return;

        const cartData = event?.cartData;
        const hasCartTotals =
          cartData &&
          typeof cartData.item_count !== 'undefined' &&
          typeof cartData.total_price !== 'undefined' &&
          typeof cartData.total_weight !== 'undefined';

        if (hasCartTotals) {
          this.applyCartData(cartData);
          this.render();
          return;
        }

        await this.hydrateCartState();
      }

      async hydrateCartState() {
        if (!this.state?.cart || this.isHydratingCart) return;

        this.isHydratingCart = true;
        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;

        try {
          const response = await fetch(cartUrl, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) return;

          const cartData = await response.json();
          this.applyCartData(cartData);
          this.render();
        } catch (error) {
          console.warn('Shipping estimator cart hydration failed', error);
        } finally {
          this.isHydratingCart = false;
        }
      }

      applyCartData(cartData) {
        if (!this.state?.cart || !cartData) return;

        this.state.cart.itemCount = Number(cartData.item_count || 0);
        this.state.cart.totalWeightGrams = Number(cartData.total_weight || 0);
        this.state.cart.totalPriceCents = Number(cartData.total_price || 0);
      }

      filterOptions(value) {
        if (!this.elements.list) return;

        const searchValue = this.normalize(value || '');
        const options = Array.from(this.elements.list.querySelectorAll('[data-country-code]'));
        let visibleCount = 0;

        options.forEach((option) => {
          const matches = this.normalize(option.textContent).includes(searchValue);
          option.parentElement.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        this.elements.reset?.classList.toggle('hidden', !searchValue);
        this.elements.empty?.classList.toggle('hidden', visibleCount > 0);

        if (this.elements.liveRegion) {
          this.elements.liveRegion.textContent =
            visibleCount > 0 ? `${visibleCount}` : this.strings.searchEmpty || '';
        }
      }

      getVisibleOptions() {
        return Array.from(this.elements.list?.querySelectorAll('[data-country-code]') || []).filter(
          (option) => !option.parentElement.hidden
        );
      }

      syncActivedescendant(option) {
        if (!this.elements.search) return;
        this.elements.search.setAttribute('aria-activedescendant', option?.id || '');
      }

      selectCountry(countryCode) {
        if (!countryCode) return;

        this.selectedCountryCode = countryCode;
        this.renderSelectedCountry();

        if (this.elements.countryInput && this.localizationForm && countryCode !== this.state.countryCode) {
          this.elements.countryInput.value = countryCode;
          this.classList.add('is-loading');
          this.localizationForm.submit();
          return;
        }

        this.render();
        this.closePanel();
      }

      render() {
        if (!this.shell) return;

        if (this.context !== 'product' && Number(this.state.cart?.itemCount || 0) === 0) {
          this.shell.hidden = true;
          return;
        }

        this.renderSelectedCountry();
        this.syncSelectedOptionState();

        const result = this.calculateShipping(this.selectedCountryCode);

        if (this.context === 'product') {
          this.renderProduct(result);
        } else if (this.context === 'notification') {
          this.renderNotification(result);
        } else {
          this.renderCart(result);
        }

        this.shell.hidden = false;
      }

      renderSelectedCountry() {
        const countryName = this.getCountryName(this.selectedCountryCode);
        this.elements.selectedCountry.forEach((element) => {
          element.textContent = countryName;
        });
      }

      syncSelectedOptionState() {
        this.elements.list?.querySelectorAll('[data-country-code]').forEach((option) => {
          const isSelected = option.dataset.countryCode === this.selectedCountryCode;
          option.setAttribute('aria-selected', isSelected.toString());
        });
      }

      renderProduct(result) {
        if (!this.elements.primary) return;

        if (this.elements.rateRow) this.toggleHidden(this.elements.rateRow, true);
        this.renderMessage('', 'default');
        this.toggleHidden(this.elements.progressBlock, true);
        this.setProgressValue(0);

        if (result.status === 'no_shipping' || result.status === 'no_weight') {
          this.renderMessage(result.status === 'no_shipping' ? this.strings.noShipping : this.strings.noWeight);
          return;
        }

        if (!result.cheapestRate) return;

        if (this.elements.rateName) {
          this.elements.rateName.textContent = this.localizeRateName(result.cheapestRate.name || this.strings.title);
        }

        if (this.elements.rateDetail) {
          const deliveryDetails = this.repairMojibakeText(result.cheapestRate.delivery_details || '').trim();
          this.elements.rateDetail.textContent = deliveryDetails;
          this.toggleHidden(this.elements.rateDetail, !deliveryDetails);
        }

        this.elements.primary.textContent = result.cheapestRate.isFree
          ? this.strings.freeShipping
          : this.formatMoney(
              result.cheapestRate.displayPrice ?? result.cheapestRate.price,
              result.cheapestRate.currency || result.zone.currency
            );

        if (this.elements.rateRow) this.toggleHidden(this.elements.rateRow, false);

        if (!result.threshold.canShow) return;

        if (result.threshold.freeShippingActive) {
          this.renderMessage(this.getFreeShippingSuccessMessage(), 'success');
          this.toggleHidden(this.elements.progressBlock, false);
          this.setProgressValue(100);
          return;
        }

        if (result.threshold.remaining > 0) {
          this.renderMessage(
            this.interpolate(this.strings.freeShippingRemaining, {
              amount: this.formatMoney(result.threshold.remaining, result.zone.currency),
            })
          );
          this.toggleHidden(this.elements.progressBlock, false);
          this.setProgressValue(result.threshold.progress);
        }
      }

      renderCart(result) {
        this.resetCartLikeView();

        if (result.status === 'no_shipping' || result.status === 'no_weight') {
          this.renderMessage(result.status === 'no_shipping' ? this.strings.noShipping : this.strings.noWeight);
          return;
        }

        this.renderRates(result.rates, result.zone.currency);

        if (result.threshold.freeShippingActive) {
          this.renderMessage(this.getFreeShippingSuccessMessage(), 'success');
          return;
        }

        if (result.threshold.canShow && result.threshold.remaining > 0) {
          const message = this.interpolate(this.strings.freeShippingRemaining, {
            amount: this.formatMoney(result.threshold.remaining, result.zone.currency),
          });

          this.toggleHidden(this.elements.progressBlock, false);
          if (this.elements.progressText) this.elements.progressText.textContent = message;
          this.setProgressValue(result.threshold.progress);
        }
      }

      renderNotification(result) {
        this.resetCartLikeView();

        if (result.status === 'no_shipping' || result.status === 'no_weight') {
          this.renderMessage(result.status === 'no_shipping' ? this.strings.noShipping : this.strings.noWeight);
          return;
        }

        this.renderRates(result.cheapestRate ? [result.cheapestRate] : [], result.zone.currency);

        if (result.threshold.freeShippingActive) {
          this.renderMessage(this.getFreeShippingSuccessMessage(), 'success');
          return;
        }

        if (result.threshold.canShow && result.threshold.remaining > 0) {
          const message = this.interpolate(this.strings.freeShippingRemaining, {
            amount: this.formatMoney(result.threshold.remaining, result.zone.currency),
          });

          this.toggleHidden(this.elements.progressBlock, false);
          if (this.elements.progressText) this.elements.progressText.textContent = message;
          this.setProgressValue(result.threshold.progress);
        }
      }

      resetCartLikeView() {
        if (this.elements.rates) {
          this.elements.rates.innerHTML = '';
        }

        if (this.elements.progressText) {
          this.elements.progressText.textContent = '';
        }

        this.renderMessage('', 'default');
        this.toggleHidden(this.elements.progressBlock, true);
        this.setProgressValue(0);
      }

      renderRates(rates, currency) {
        if (!this.elements.rates) return;

        this.elements.rates.innerHTML = (rates || [])
          .map(
            (rate) => `
              <div class="shipping-estimator__rate">
                <div class="shipping-estimator__rate-content">
                  <span class="shipping-estimator__rate-name">${this.escapeHtml(this.localizeRateName(rate.name))}</span>
                  ${
                    rate.delivery_details
                      ? `<span class="shipping-estimator__rate-detail">${this.escapeHtml(
                          this.repairMojibakeText(rate.delivery_details)
                        )}</span>`
                      : ''
                  }
                </div>
                <span class="shipping-estimator__rate-price">${this.escapeHtml(
                  rate.isFree
                    ? this.strings.freeShipping
                    : this.formatMoney(rate.displayPrice ?? rate.price, rate.currency || currency)
                )}</span>
              </div>
            `
          )
          .join('');
      }

      renderMessage(message, tone = 'default') {
        if (!this.elements.message) return;
        this.elements.message.textContent = message;
        this.elements.message.classList.toggle('shipping-estimator__message--success', tone === 'success');
        this.toggleHidden(this.elements.message, !message);
      }

      setProgressValue(progress) {
        const safeProgress = Math.min(Math.max(Number(progress) || 0, 0), 100);
        const trackBackground = `linear-gradient(90deg, rgb(146, 206, 62) 0%, rgb(146, 206, 62) ${safeProgress}%, rgba(var(--color-foreground), 0.12) ${safeProgress}%, rgba(var(--color-foreground), 0.12) 100%)`;

        if (this.elements.progress) {
          this.elements.progress.style.background = trackBackground;
        }

        if (this.elements.progressFill) {
          this.elements.progressFill.style.width = `${safeProgress}%`;
          this.elements.progressFill.style.opacity = safeProgress > 0 ? '1' : '0';
          this.elements.progressFill.style.background =
            'linear-gradient(90deg, rgb(146, 206, 62) 0%, rgb(126, 178, 48) 100%)';
        }

        if (this.elements.progressGoal) {
          this.elements.progressGoal.classList.toggle('is-active', safeProgress >= 100);
        }
      }

      getFreeShippingSuccessMessage() {
        return this.strings.freeShippingSuccess || this.getDefaultStrings().freeShippingSuccess;
      }

      toggleHidden(element, shouldHide) {
        if (!element) return;
        element.hidden = shouldHide;
      }

      calculateShipping(countryCode) {
        const zone = this.findZone(countryCode);

        if (!zone) {
          return { status: 'no_shipping' };
        }

        const totals = this.context === 'product' ? this.getProductTotals() : this.getCartTotals();
        const zoneRates = [
          ...(Array.isArray(zone.rates) ? zone.rates : []),
          ...(Array.isArray(zone.free_rates) ? zone.free_rates : []),
        ];
        const rates = [];
        const threshold = this.getThresholdState(zone);
        const rateFreeShippingActive = threshold.freeShippingActive;
        let hasCarrierRates = false;
        let hasWeightRates = false;

        zoneRates.forEach((rate) => {
          if (rate.type === 'carrier') {
            hasCarrierRates = true;
            return;
          }

          if (rate.type === 'weight') {
            hasWeightRates = true;
            if (!this.matchesWeightRate(rate, totals.weightKg)) return;
          }

          if (rate.type === 'price' && !this.matchesPriceRate(rate, totals.subtotal)) {
            return;
          }

          if (rate.type === 'price' || rate.type === 'weight') {
            rates.push({
              name: rate.name || '',
              delivery_details: rate.delivery_details || '',
              price: Number(rate.price || 0),
              displayPrice: Number(rate.price || 0),
              currency: rate.currency || zone.currency,
              isFree: Number(rate.price || 0) === 0,
            });
          }
        });

        const dedupedRates = Array.from(
          rates.reduce((map, rate) => {
            const existingRate = map.get(rate.name);
            if (!existingRate || rate.price < existingRate.price) {
              map.set(rate.name, rate);
            }
            return map;
          }, new Map()).values()
        );

        dedupedRates.sort((left, right) => {
          if (left.price !== right.price) return left.price - right.price;
          return left.name.localeCompare(right.name, this.locale);
        });

        if (!dedupedRates.length) {
          return { status: hasCarrierRates || hasWeightRates ? 'no_weight' : 'no_shipping' };
        }

        if (rateFreeShippingActive && !dedupedRates.some((rate) => rate.isFree)) {
          const cheapestIndex = dedupedRates.findIndex((rate) => rate.price > 0);
          if (cheapestIndex >= 0) {
            dedupedRates[cheapestIndex] = {
              ...dedupedRates[cheapestIndex],
              displayPrice: 0,
              isFree: true,
            };
          }
        }

        return {
          status: 'ok',
          zone,
          totals,
          rates: dedupedRates,
          cheapestRate: dedupedRates[0],
          threshold,
          rateFreeShippingActive,
        };
      }

      getProductTotals() {
        const variantId = `${this.state.product?.selectedVariantId || ''}`;
        const variant = this.state.product?.variants?.[variantId] || {};
        const quantity = this.getProductQuantity();
        const linePriceCents = Number(variant.priceCents || 0) * quantity;
        const lineWeightGrams = Number(variant.weightGrams || 0) * quantity;

        return {
          quantity,
          subtotal: linePriceCents / 100,
          weightKg: lineWeightGrams / 1000,
        };
      }

      getThresholdState(zone) {
        if (typeof zone.free_threshold !== 'number' || zone.free_threshold <= 0) {
          return {
            canShow: false,
            freeShippingActive: false,
            remaining: null,
            progress: 0,
          };
        }

        const subtotal = this.getCartTotals().subtotal;
        const remaining = Math.max(zone.free_threshold - subtotal, 0);
        const progress = Math.min(Math.max(Math.round((subtotal / zone.free_threshold) * 100), 0), 100);

        return {
          canShow: true,
          freeShippingActive: subtotal >= zone.free_threshold,
          remaining,
          progress,
        };
      }

      getProductQuantity() {
        const quantityInput =
          this.productInfo?.querySelector(`.quantity__input[form="product-form-${this.dataset.sectionId}"]`) ||
          this.productInfo?.querySelector(`#Quantity-${this.dataset.sectionId}`);
        const quantity = Number(quantityInput?.value || this.state.product?.quantity || 1);

        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getCartTotals() {
        const totalPriceCents = Number(this.state.cart?.totalPriceCents || 0);
        const totalWeightGrams = Number(this.state.cart?.totalWeightGrams || 0);

        return {
          subtotal: totalPriceCents / 100,
          weightKg: totalWeightGrams / 1000,
        };
      }

      findZone(countryCode) {
        const matchingZones = (this.config?.zones || []).filter(
          (zone) => Array.isArray(zone.countries) && zone.countries.includes(countryCode)
        );

        if (!matchingZones.length) return null;

        const preferredZone = this.config?.country_default_zones?.[countryCode];
        const configuredZone = preferredZone && matchingZones.find((zone) => zone.id === preferredZone || zone.name === preferredZone);
        if (configuredZone) return configuredZone;

        matchingZones.sort((left, right) => {
          const leftCountries = Array.isArray(left.countries) ? left.countries.length : Number.MAX_SAFE_INTEGER;
          const rightCountries = Array.isArray(right.countries) ? right.countries.length : Number.MAX_SAFE_INTEGER;

          if (leftCountries !== rightCountries) return leftCountries - rightCountries;

          const leftProvinces = left.country_provinces?.[countryCode];
          const rightProvinces = right.country_provinces?.[countryCode];
          if (Array.isArray(leftProvinces) && Array.isArray(rightProvinces) && leftProvinces.length !== rightProvinces.length) {
            const leftCoverage = leftProvinces.length || Number.MAX_SAFE_INTEGER;
            const rightCoverage = rightProvinces.length || Number.MAX_SAFE_INTEGER;
            return rightCoverage - leftCoverage;
          }

          const leftRates = Array.isArray(left.rates) ? left.rates.length : 0;
          const rightRates = Array.isArray(right.rates) ? right.rates.length : 0;

          return rightRates - leftRates;
        });

        return matchingZones[0];
      }

      matchesPriceRate(rate, subtotal) {
        const min = typeof rate.min_order === 'number' ? rate.min_order : null;
        const max = typeof rate.max_order === 'number' ? rate.max_order : null;

        if (min !== null && subtotal < min) return false;
        if (max !== null && subtotal > max) return false;
        return true;
      }

      matchesWeightRate(rate, weightKg) {
        const min = typeof rate.min_kg === 'number' ? rate.min_kg : 0;
        const max = typeof rate.max_kg === 'number' ? rate.max_kg : null;

        if (weightKg < min) return false;
        if (max !== null && weightKg > max) return false;
        return true;
      }

      formatMoney(amount, currency) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency,
          }).format(amount);
        } catch (error) {
          return `${currency} ${amount.toFixed(2)}`;
        }
      }

      getCountryName(countryCode) {
        const knownCountry = this.countries.find((country) => country.code === countryCode);
        return (
          this.displayNames?.of(countryCode) ||
          knownCountry?.name ||
          (countryCode === this.state.countryCode ? this.state.currentCountryName : countryCode)
        );
      }

      localizeRateName(rateName) {
        const normalizedName = this.repairMojibakeText(rateName || '').trim();
        if (!normalizedName) return '';

        const serviceMatch = normalizedName.match(/^(.+?)\s*\((.+)\)$/);

        if (serviceMatch) {
          const service = this.localizeServiceLabel(serviceMatch[1]);
          const time = this.localizeTransitTime(serviceMatch[2]);
          return `${service} (${time})`;
        }

        const localizedTransit = this.localizeTransitTime(normalizedName);
        if (localizedTransit !== normalizedName) return localizedTransit;

        return this.localizeServiceLabel(normalizedName);
      }

      localizeServiceLabel(value) {
        const normalizedValue = this.normalize(this.repairMojibakeText(value || '').trim());
        if (!normalizedValue) return '';

        const aliases = {
          economy: [/^economy$/, /^economia$/, /^economique$/, /^economico$/, /^economica$/],
          express: [/^express$/, /^espresso$/],
          standard: [/^standard$/],
          priority: [/^priority$/, /^prioritaire$/, /^prioridad$/, /^priorita$/, /^prioridade$/],
        };

        const translations = {
          it: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          de: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          fr: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          es: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          pt: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          nl: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          pl: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          cs: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          sk: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          hu: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
          en: { economy: 'Economy', express: 'Express', standard: 'Standard', priority: 'Priority' },
        };

        const key = Object.keys(aliases).find((serviceKey) =>
          aliases[serviceKey].some((pattern) => pattern.test(normalizedValue))
        );

        if (!key) {
          return this.repairMojibakeText(value || '').trim();
        }

        return (translations[this.getLanguageCode()] || translations.en)[key];
      }

      localizeTransitTime(value) {
        const normalizedValue = this.repairMojibakeText(value || '').trim();
        if (!normalizedValue) return '';

        const compactValue = normalizedValue.replace(/\s+/g, ' ');
        const rangeMatch = compactValue.match(/(\d+)\s*(?:-|–|—|to|a|bis|à|hasta|tot|até|do|до|~|〜)\s*(\d+)/i);

        if (rangeMatch && this.containsWorkingDayToken(compactValue)) {
          return this.formatWorkingDayRange(rangeMatch[1], rangeMatch[2]);
        }

        const singleMatch = compactValue.match(/(\d+)/);
        if (singleMatch && this.containsWorkingDayToken(compactValue)) {
          return this.formatWorkingDayCount(singleMatch[1]);
        }

        return compactValue;
      }

      containsWorkingDayToken(value) {
        return /(working\s*days?|working\s*day|giorni?\s*lavorativi|gg\s*lavorativi|jours?\s*ouvr(?:ables|és)|d[ií]as?\s*laborables|dias?\s*(?:uteis|úteis)|werkdagen|werktage|munkanap|pracovn(?:í|ych)\s*dn(?:y|i|ů)|dni\s*robocze|virkedager|arbetsdag(?:ar)?|iş\s*günü|radnih?\s*dana|рабоч(?:их|ие)?\s*дн(?:ей|я)?|工作日|営業日|วันทำการ)/i.test(
          value
        );
      }

      formatWorkingDayRange(from, to) {
        const language = this.getLanguageCode();
        const formatters = {
          it: `${from}-${to} giorni lavorativi`,
          de: `${from} bis ${to} Werktage`,
          fr: `${from} à ${to} jours ouvrables`,
          es: `${from} a ${to} días laborables`,
          pt: `${from} a ${to} dias úteis`,
          nl: `${from} tot ${to} werkdagen`,
          pl: `${from}-${to} dni roboczych`,
          cs: `${from} až ${to} pracovních dnů`,
          sk: `${from} až ${to} pracovných dní`,
          hu: `${from}-${to} munkanap`,
          en: `${from} to ${to} working days`,
        };

        return formatters[language] || formatters.en;
      }

      formatWorkingDayCount(days) {
        const language = this.getLanguageCode();
        const formatters = {
          it: `${days} giorni lavorativi`,
          de: `${days} Werktage`,
          fr: `${days} jours ouvrables`,
          es: `${days} días laborables`,
          pt: `${days} dias úteis`,
          nl: `${days} werkdagen`,
          pl: `${days} dni roboczych`,
          cs: `${days} pracovních dnů`,
          sk: `${days} pracovných dní`,
          hu: `${days} munkanap`,
          en: `${days} working days`,
        };

        return formatters[language] || formatters.en;
      }

      repairMojibakeText(value) {
        const input = String(value || '');
        if (!/[ÃÂ]/.test(input)) return input;

        try {
          const bytes = Uint8Array.from(Array.from(input).map((character) => character.charCodeAt(0)));
          return new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
          return input;
        }
      }

      interpolate(template, replacements) {
        return Object.entries(replacements).reduce((output, [key, value]) => {
          return output.replace(`{{ ${key} }}`, value).replace(`[${key}]`, value);
        }, template);
      }

      normalize(value) {
        return value
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
